import { Component, OnInit, OnDestroy } from '@angular/core';
import { DatePipe, NgClass, NgFor, NgIf } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Subscription } from 'rxjs';
import { Offcanvas } from 'bootstrap';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import { CKEditorModule } from '@ckeditor/ckeditor5-angular';
import { BreadcrumbComponent } from '../../elements/breadcrumb/breadcrumb.component';
import { HttpServiceService } from '../../services/http-service.service';
import { LoadingService } from '../../services/loading.service';
import { LocalStorageService } from '../../services/local-storage.service';
import { NgxToastrService } from '../../_services/ngx-toastr/ngx-toastr.service';
import { USER_PERMISSIONS } from '../../app.constants';
import { Lead, LeadComment, LeadHistory, leadSourceClass, leadSourceWebsiteClass } from '../lead.model';
import { TasksListComponent } from '../../tasks/tasks-list/tasks-list.component';

@Component({
  selector: 'app-lead-view',
  standalone: true,
  imports: [
    BreadcrumbComponent,
    NgIf,
    NgFor,
    NgClass,
    FormsModule,
    ReactiveFormsModule,
    CKEditorModule,
    DatePipe,
    TasksListComponent
  ],
  templateUrl: './lead-view.component.html',
  styleUrls: ['./lead-view.component.css']
})
export class LeadViewComponent implements OnInit, OnDestroy {
  breadcrumbList = {
    breadcrumb_path: 'Leads',
    currentURL: 'View Lead',
    breadcrumb_url: '/admin/leads'
  };

  leadId: string = '';
  lead: Lead | null = null;
  notesText: string = '';
  comments: LeadComment[] = [];
  history: LeadHistory[] = [];
  activeTab: 'comments' | 'history' = 'comments';
  commentForm!: FormGroup;
  public Editor: any = ClassicEditor;

  canViewComments: boolean = false;
  canAddComments: boolean = false;
  canViewTasks: boolean = false;

  private commentDrawer!: Offcanvas;
  private subscriptions: Subscription = new Subscription();

  /** Filter state handed over by the list page, replayed on Back. */
  private listFilters: Params = {};

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private httpService: HttpServiceService,
    public loadingService: LoadingService,
    private localStorageService: LocalStorageService,
    private fb: FormBuilder,
    private alertService: NgxToastrService
  ) {
    this.initCommentForm();
  }

  sourceClass = leadSourceClass;
  websiteClass = leadSourceWebsiteClass;

  ngOnInit(): void {
    this.checkPermissions();

    this.subscriptions.add(
      this.route.queryParams.subscribe((params) => {
        this.listFilters = params;
      })
    );

    const paramSub = this.route.paramMap.subscribe(params => {
      this.leadId = params.get('id') || '';
      if (this.leadId) {
        this.getLeadDetails();
        if (this.canViewComments) {
          this.getComments();
        }
        this.getHistory();
      }
    });
    this.subscriptions.add(paramSub);
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    if (this.commentDrawer) {
      this.commentDrawer.dispose();
    }
  }

  checkPermissions(): void {
    this.canViewComments = this.localStorageService.checkPermission(USER_PERMISSIONS.LEADS.LEADS_VIEW_COMMENTS);
    this.canAddComments = this.localStorageService.checkPermission(USER_PERMISSIONS.LEADS.LEADS_ADD_COMMENTS);
    this.canViewTasks = this.localStorageService.checkPermission(USER_PERMISSIONS.TASKS.TASKS_PAGE);
  }

  initCommentForm(): void {
    this.commentForm = this.fb.group({
      comment: ['', Validators.required]
    });
  }

  getLeadDetails(): void {
    this.loadingService.show();
    const sub = this.httpService.getLeadDetailsById(this.leadId).subscribe({
      next: (response: any) => {
        this.loadingService.hide();
        if (response.success) {
          this.lead = response.data;
          this.notesText = this.lead?.looking_for || '';
        }
      },
      error: (error) => {
        this.loadingService.hide();
        console.error('Error fetching lead details:', error);
        this.alertService.error('Error fetching lead details', 'toast-top-right');
      }
    });
    this.subscriptions.add(sub);
  }

  getComments(): void {
    const sub = this.httpService.getLeadComments(this.leadId).subscribe({
      next: (response: any) => {
        if (response.success) {
          this.comments = response.data;
        }
      },
      error: (error) => {
        console.error('Error fetching comments:', error);
      }
    });
    this.subscriptions.add(sub);
  }

  getHistory(): void {
    const sub = this.httpService.getLeadHistory(this.leadId).subscribe({
      next: (response: any) => {
        if (response.success) {
          this.history = response.data;
        }
      },
      error: (error) => {
        console.error('Error fetching history:', error);
      }
    });
    this.subscriptions.add(sub);
  }

  setActiveTab(tab: 'comments' | 'history'): void {
    this.activeTab = tab;
  }

  openCommentDrawer(): void {
    const drawerElement = document.getElementById('commentDrawer');
    if (drawerElement) {
      this.commentDrawer = new Offcanvas(drawerElement);
      this.commentDrawer.show();
    }
  }

  closeCommentDrawer(): void {
    if (this.commentDrawer) {
      this.commentDrawer.hide();
      this.commentForm.reset();
    }
  }

  saveComment(): void {
    if (this.commentForm.valid) {
      this.loadingService.show();
      const sub = this.httpService.saveLeadComment(this.leadId, this.commentForm.value).subscribe({
        next: (response: any) => {
          this.loadingService.hide();
          if (response.success) {
            this.alertService.success(response.message || 'Comment added successfully', 'toast-top-right');
            this.closeCommentDrawer();
            this.getComments();
          }
        },
        error: (error) => {
          this.loadingService.hide();
          console.error('Error saving comment:', error);
          this.alertService.error('Error saving comment', 'toast-top-right');
        }
      });
      this.subscriptions.add(sub);
    }
  }

  saveNotes(): void {
    this.loadingService.show();
    const sub = this.httpService.updateLead(this.leadId, { looking_for: this.notesText }).subscribe({
      next: (response: any) => {
        this.loadingService.hide();
        if (response.success) {
          this.alertService.success('Notes saved successfully', 'toast-top-right');
          if (this.lead) {
            this.lead.looking_for = this.notesText;
          }
        }
      },
      error: () => {
        this.loadingService.hide();
        this.alertService.error('Error saving notes', 'toast-top-right');
      }
    });
    this.subscriptions.add(sub);
  }

  backToLeads(): void {
    this.router.navigate(['/admin/leads'], { queryParams: this.listFilters });
  }
}
